import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/CordwoodHistory';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const CORDWOOD_HISTORY_API_REDUCER_KEY = 'cordwood-histories';
export const CORDWOOD_HISTORY_PATH = 'cordwood-histories';

export const cordwoodHistoryApi = createApi({
    reducerPath: CORDWOOD_HISTORY_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['CordwoodHistory', 'CordwoodHistories'],
    endpoints: (builder) => ({
        getCordwoodHistoryList: builder.query<PaginationResult<Entity.CordwoodHistory>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['CordwoodHistories'],
        }),
        getCordwoodHistory: builder.query<Entity.CordwoodHistory, Entity.PrimaryCordwoodHistory>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = cordwoodHistoryNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ CORDWOOD_HISTORY_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['CordwoodHistory'],
        }),
        getCordwoodHistoryMultiple: builder.query<Entity.CordwoodHistory, Entity.PrimaryCordwoodHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }/ids${Entity.PrimaryCordwoodHistory.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['CordwoodHistories'],
        }),
        getCordwoodHistoryPresentation: builder.query<PaginationResult<Entity.CordwoodHistory>, Entity.PresentationCordwoodHistoryRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ CORDWOOD_HISTORY_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['CordwoodHistories'],
        }),
        getCordwoodHistoryByContext: builder.query<PaginationResult<Entity.CordwoodHistory>, Entity.FilterByContextRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }/find/byContext?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['CordwoodHistories'],
        }),
        
        insertCordwoodHistory: builder.mutation<Entity.CordwoodHistory, Entity.CreateCordwoodHistory>({
            query: (toInsert) => {
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    cordwoodHistoryApi.util.upsertQueryData('getCordwoodHistory', Entity.PrimaryCordwoodHistory.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['CordwoodHistories'],
        }),
        insertMultipleCordwoodHistory: builder.mutation<Entity.CordwoodHistory[], Entity.CreateCordwoodHistory[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            cordwoodHistoryApi.util.upsertQueryData('getCordwoodHistory', Entity.PrimaryCordwoodHistory.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['CordwoodHistories'],
        }),
        
        deleteCordwoodHistory: builder.mutation<Entity.CordwoodHistory, Entity.CordwoodHistory>({
            query: (toDelete) => {
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }/${Entity.PrimaryCordwoodHistory.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['CordwoodHistories'],
        }),
        deleteMultipleCordwoodHistory: builder.mutation<Entity.CordwoodHistory[], Entity.PrimaryCordwoodHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ CORDWOOD_HISTORY_PATH }/batch${Entity.PrimaryCordwoodHistory.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['CordwoodHistories'],
        }),
    }),
})

export const [cordwoodHistoryNormalizer, cordwoodHistoryNormalizerMiddleware] = createNormalizationMiddleware(CORDWOOD_HISTORY_API_REDUCER_KEY, cordwoodHistoryApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetCordwoodHistoryListQuery,
    useGetCordwoodHistoryMultipleQuery,
    useGetCordwoodHistoryPresentationQuery,
    useGetCordwoodHistoryQuery,
    useInsertCordwoodHistoryMutation,
    useInsertMultipleCordwoodHistoryMutation,
    
    useDeleteCordwoodHistoryMutation,
    useDeleteMultipleCordwoodHistoryMutation,
    useGetCordwoodHistoryByContextQuery,
    
} = cordwoodHistoryApi
